import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

const CAPTURE_SOURCE = 'capture-location-marker'
const RINGS_BEFORE_LAYER = 'unclustered-point'
const MODEL_LAYER_PREFIX = 'trees-3d-'
const RING_LAYER_IDS = ['capture-ring-outer', 'capture-ring-mid', 'capture-ring-inner']

/**
 * Muestra tres aros concéntricos animados (tonos verdes) alrededor
 * de la ubicación capturada (coords) cuando hay coordenadas confirmadas.
 *
 * @param {{ map, layersReady }} params
 */
export const useCaptureLocationRings = ({ map, layersReady }) => {
  const coords = useSelector(state => state.mapState.coords)
  const animFrameRef = useRef(null)

  // coords viene como { lng, lat } del store; GeoJSON necesita [lng, lat]
  const lngLat = useMemo(
    () => (coords?.lng != null ? [coords.lng, coords.lat] : null),
    [coords?.lng, coords?.lat],
  )
  const isActive = lngLat != null

  useEffect(() => {
    if (!map || !layersReady) return
    const isStyleReady = () => {
      try {
        return Boolean(map.getStyle?.()) && map.isStyleLoaded()
      } catch {
        return false
      }
    }
    if (!isStyleReady()) return

    // 1. Source dedicado para la ubicación capturada
    try {
      if (!map.getSource(CAPTURE_SOURCE)) {
        map.addSource(CAPTURE_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
      }
    } catch (_) {
      return
    }

    const getRingAnchorLayer = () => {
      const layers = map.getStyle?.()?.layers || []
      const firstModelLayerId = layers.find(layer => layer?.id?.startsWith(MODEL_LAYER_PREFIX))?.id
      if (firstModelLayerId && map.getLayer(firstModelLayerId)) return firstModelLayerId
      if (map.getLayer(RINGS_BEFORE_LAYER)) return RINGS_BEFORE_LAYER
      return undefined
    }

    // 2. Crear layers si no existen.
    // Preferimos anclar antes del primer modelo 3D para que los aros queden
    // por debajo del 3D, y encima del satelital.
    const beforeLayer = getRingAnchorLayer()
    const enforceRingOrder = () => {
      const targetLayer = getRingAnchorLayer()
      if (!targetLayer) return
      RING_LAYER_IDS.forEach(layerId => {
        if (!map.getLayer(layerId)) return
        try {
          map.moveLayer(layerId, targetLayer)
        } catch (_) {}
      })
    }

    if (!map.getLayer('capture-ring-outer')) {
      try {
        map.addLayer(
          {
            id: 'capture-ring-outer',
            type: 'circle',
            slot: 'top',
            source: CAPTURE_SOURCE,
            paint: {
              'circle-radius': 23,
              'circle-color': '#ffffff',
              'circle-opacity': 0,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-opacity': 0.9,
              'circle-emissive-strength': 1,
            },
          },
          beforeLayer
        )
      } catch (_) {}
    }

    if (!map.getLayer('capture-ring-mid')) {
      try {
        map.addLayer(
          {
            id: 'capture-ring-mid',
            type: 'circle',
            slot: 'top',
            source: CAPTURE_SOURCE,
            paint: {
              'circle-radius': 16,
              'circle-color': '#00e5ff',
              'circle-opacity': 0,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#00e5ff',
              'circle-stroke-opacity': 1,
              'circle-emissive-strength': 1,
            },
          },
          beforeLayer
        )
      } catch (_) {}
    }

    if (!map.getLayer('capture-ring-inner')) {
      try {
        map.addLayer(
          {
            id: 'capture-ring-inner',
            type: 'circle',
            slot: 'top',
            source: CAPTURE_SOURCE,
            paint: {
              'circle-radius': 10,
              'circle-color': '#ffffff',
              'circle-opacity': 0,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-opacity': 0.9,
              'circle-emissive-strength': 1,
            },
          },
          beforeLayer
        )
      } catch (_) {}
    }

    enforceRingOrder()

    // 3. Actualizar datos del source
    const source = map.getSource(CAPTURE_SOURCE)
    if (source) {
      if (isActive) {
        source.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: lngLat },
              properties: {},
            },
          ],
        })
      } else {
        source.setData({ type: 'FeatureCollection', features: [] })
      }
    }

    // 4. Animación: aro exterior pulsa (ciclo 2s)
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = null
    }

    if (isActive) {
      let startTime = null

      const animate = timestamp => {
        if (!startTime) startTime = timestamp
        const t = ((timestamp - startTime) % 2000) / 2000
        const opacity = 0.7 + 0.3 * Math.sin(t * Math.PI * 2)

        try {
          if (map.getLayer('capture-ring-outer')) {
            map.setPaintProperty('capture-ring-outer', 'circle-stroke-opacity', opacity)
          }
        } catch (_) {}

        animFrameRef.current = requestAnimationFrame(animate)
      }

      animFrameRef.current = requestAnimationFrame(animate)
    }

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = null
      }
    }
  }, [map, layersReady, isActive, lngLat])
}

export default useCaptureLocationRings
